import React from 'react'
import { useTranslation } from 'react-i18next'

import { Build, FiberNew, Flag, Palette as PaletteIcon } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { LiveEventEvolutionTypeEnum } from '../../types/LiveEventCollection'
import { LIVE_EVENT_EVOLUTION_TYPE_COLOR } from '../LiveEventEvolutionTypeChip/LiveEventEvolutionTypeChip'

/**
 * Labelled outlined pill for a blueprint version's evolution type — the Blueprint
 * tab timeline variant of `LiveEventEvolutionTypeChip` (which stays icon-only for
 * the calendar surfaces that already use it).
 */
export type LiveEventEvolutionTagType =
  | LiveEventEvolutionTypeEnum.Highlighted
  | LiveEventEvolutionTypeEnum.FirstInstanceSeen
  | LiveEventEvolutionTypeEnum.MechanicChange
  | LiveEventEvolutionTypeEnum.Reskin

interface LiveEventEvolutionTypeTagProps {
  type: LiveEventEvolutionTagType
  /** Keeps the tag's column open without showing it, so rows line up down the list. */
  placeholder?: boolean
}

export const LiveEventEvolutionTypeTag: React.FC<LiveEventEvolutionTypeTagProps> = ({ type, placeholder = false }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const config: { [key in LiveEventEvolutionTagType]: { color: string; icon: React.ReactNode; label: string } } = {
    [LiveEventEvolutionTypeEnum.Highlighted]: {
      color: theme.palette.secondary.main,
      icon: <FiberNew sx={{ fontSize: '14px' }} />,
      label: t('live-events:live_event_evolution_type_first_appearance'),
    },
    [LiveEventEvolutionTypeEnum.FirstInstanceSeen]: {
      color: theme.palette.primary.main,
      icon: <Flag sx={{ fontSize: '12px' }} />,
      label: t('live-events:live_event_evolution_type_first_seen'),
    },
    [LiveEventEvolutionTypeEnum.MechanicChange]: {
      // Canonical mechanic-change colour, shared with the icon-only chip and Market Analytics.
      color: LIVE_EVENT_EVOLUTION_TYPE_COLOR[LiveEventEvolutionTypeEnum.MechanicChange] as string,
      icon: <Build sx={{ fontSize: '12px' }} />,
      label: t('live-events:live_event_evolution_type_mechanic_change'),
    },
    [LiveEventEvolutionTypeEnum.Reskin]: {
      color: theme.palette.success.main,
      icon: <PaletteIcon sx={{ fontSize: '12px' }} />,
      label: t('live-events:live_event_evolution_type_reskin'),
    },
  }

  const { color, icon, label } = config[type]

  return (
    <Box
      component="span"
      aria-hidden={placeholder || undefined}
      sx={{
        visibility: placeholder ? 'hidden' : 'visible',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        flexShrink: 0,
        padding: '2px 9px',
        border: `1px solid ${color}`,
        borderRadius: borderRadiusDefault,
        color,
        fontSize: '11.5px',
        fontWeight: 'bold',
        lineHeight: 1.6,
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      {label}
    </Box>
  )
}
